/**************************************************************************************************
 *
 *       @file ./auth-panel.tsx
 *
 *       Dynamic content area - can contain:
 *           login
 *           signup (create-account)
 *           forgot-password
 *           email-verification << DEV ONLY
 *           email-verification-fail
 *           email-verification-success
 *           password-reset
 *           password-reset-success
 *           unlock-account-expired
 *           unlock-account-success
 *
 */
/************************************** THIRD-PARTY IMPORTS ***************************************/
import React from 'react';
import {inject} from 'mobx-react';
import {Route, Switch} from 'react-router-dom';
import {getLangFromUrlPathname, switchExpr, getQueryString} from 'mad-utils/lib/react';
import {releaseEnvAbbrev} from 'env-var-helpers';

const lang = getLangFromUrlPathname(window.location.href);

/**************************************** PROJECT IMPORTS *****************************************/
import {AuthPanelRootView} from './auth-panel/auth-panel-root-view';
import {EmailVerificationCardContainer} from './auth-panel/email-verification/email-verification-card-container';
import {LoadMessage, ConfigProps} from '@canimmunize/core-imports';

/**************************************** DYNAMIC IMPORTS *****************************************/
import Loadable from 'react-loadable';
import {checkRedirectToCanvasAuth} from './auth-panel/canvas/canvas-utils';

const SignupContainer = Loadable({
    loader: () => import('./auth-panel/signup/signup-container').then(cmp => cmp.SignupContainer),
    loading: LoadMessage,
});

const PasswordResetCardContainer = Loadable({
    loader: () =>
        import('./auth-panel/password-reset/password-reset-card-container').then(
            cmp => cmp.PasswordResetCardContainer
        ),
    loading: LoadMessage,
});

const LoginCardContainer = Loadable({
    loader: () =>
        import('./auth-panel/login-card/login-card-container').then(cmp => cmp.LoginCardContainer),
    loading: LoadMessage,
});

const UnlockAccountExpiredContainer = Loadable({
    loader: () =>
        import('./auth-panel/unlock-account/unlock-account-expired-container').then(
            cmp => cmp.UnlockAccountExpiredContainer
        ),
    loading: LoadMessage,
});

const UnlockAccountSuccessCard = Loadable({
    loader: () =>
        import('./auth-panel/unlock-account/unlock-account-success-card').then(
            cmp => cmp.UnlockAccountSuccessCard
        ),
    loading: LoadMessage,
});

const PasswordResetSuccessCard = Loadable({
    loader: () =>
        import('./auth-panel/password-reset/password-reset-success-card').then(
            cmp => cmp.PasswordResetSuccessCard
        ),
    loading: LoadMessage,
});

const CanvasContainer = Loadable({
    loader: () => import('./auth-panel/canvas/canvas-container').then(cmp => cmp.CanvasContainer),
    loading: LoadMessage,
});

const Error404Screen = Loadable({
    loader: () => import('../error-404-screen/error-404-screen').then(cmp => cmp.Error404Screen),
    loading: LoadMessage,
});

const ForgotPasswordCardContainer = Loadable({
    loader: () =>
        import('./auth-panel/forgot-password-card/forgot-password-card-container').then(
            cmp => cmp.ForgotPasswordCardContainer
        ),
    loading: LoadMessage,
});

/******************************************** HELPERS *********************************************/
/**
 * Prefix the child path with the auth namespace (i.e. the namespace for all pre-login pages)
 * Example:
 *     Auth namespace: /auth/:lang/
 *     Full path:      /auth/:lang/forgot-password
 *
 * @param {string} childPath Path to add after the auth namespace
 * @return {string} Full path, containing both auth namespace prefix, & given child path
 */
const authNS = (childPath: string): string => `/auth/:lang/${childPath}`;

/**
 * Use if no matching path is found within the auth namespace - redirect the
 * user to the login page in this case
 */
const RedirectToLogin = ({config}: ConfigProps) => {
    const loginPageURL = config.routes.login;
    location.pathname = loginPageURL;
    return <div />;
};

/**
 * Redirect to take user to the consent statement page
 */
const RedirectToCIConsent = ({config}: ConfigProps) => {
    const consentPageURL = config.urls.canimmunizeWeb.consentStatement(getQueryString());
    const canvasRedirect = checkRedirectToCanvasAuth(false, 'signup/create-account');
    location.href = canvasRedirect || consentPageURL;
    return null;
};

/********************************************* ROUTER *********************************************/
/**
 * Routing for the pre-login app
 * Pre-loads other cards
 */
@inject('config')
export class AuthPanelRouter extends React.Component<ConfigProps> {
    componentDidMount() {
        SignupContainer.preload();
        PasswordResetCardContainer.preload();
        LoginCardContainer.preload();
        UnlockAccountExpiredContainer.preload();
        UnlockAccountSuccessCard.preload();
        PasswordResetSuccessCard.preload();
        Error404Screen.preload();
        ForgotPasswordCardContainer.preload();
    }

    render() {
        return (
            <AuthPanelRootView>
                <Switch>
                    {/* FORGOT PASSWORD */}
                    <Route
                        path={authNS(`forgot-password`)}
                        component={ForgotPasswordCardContainer}
                    />
                    <Route
                        path={authNS(`forgot-password-success`)}
                        render={() => <ForgotPasswordCardContainer loadedBySuccessPath={true} />}
                    />

                    {/* PASSWORD RESET */}
                    <Route path={authNS(`password-reset`)} component={PasswordResetCardContainer} />
                    <Route
                        path={authNS(`password-reset-success`)}
                        component={PasswordResetSuccessCard}
                    />

                    {/* EMAIL VERIFICATION */}
                    <Route
                        path={authNS(`email-verification`)}
                        component={EmailVerificationCardContainer({
                            renderComponent: 'base',
                        })}
                    />
                    <Route
                        path={authNS(`email-verification-fail`)}
                        component={EmailVerificationCardContainer({
                            renderComponent: 'fail',
                        })}
                    />
                    <Route
                        path={authNS(`email-verification-success`)}
                        component={EmailVerificationCardContainer({
                            renderComponent: 'success',
                        })}
                    />

                    {/* SIGN UP */}
                    <Route
                        exact
                        path={authNS(`signup`)}
                        render={() => <RedirectToCIConsent config={this.props.config} />}
                    />
                    <Route path={authNS(`signup/:any`)} component={SignupContainer} />

                    {/* LOGIN */}
                    <Route path={authNS(`login`)} component={LoginCardContainer} />

                    {/* UNLOCK ACCOUNT */}
                    <Route
                        path={authNS(`unlock-account-success`)}
                        component={UnlockAccountSuccessCard}
                    />
                    <Route
                        path={authNS(`unlock-account-expired`)}
                        component={UnlockAccountExpiredContainer}
                    />

                    {/* CANVAS RELATED */}
                    <Route path={authNS(`flu-vaccine-safety-survey`)} component={CanvasContainer} />

                    {/* REDIRECT TO LOGIN IF NO OTHER ROUTES MATCH */}
                    <Route render={() => <RedirectToLogin config={this.props.config} />} />
                </Switch>
            </AuthPanelRootView>
        );
    }
}
